<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { toast } from '~/utils/sonner'
import type {
  BigNewsAudienceMode,
  BigNewsCampaignPreview,
  BigNewsCampaignRequest,
} from '~/composables/useAdmin'

type DeliverySummary = {
  state?: string
  eligible_device_count?: number
  sent?: number
  failed?: number
  sender_failures?: number
  error?: string
}

type CampaignHistoryItem = {
  id: number
  title?: string
  title_en?: string
  html?: string
  html_en?: string
  content?: string
  content_en?: string
  expired?: boolean
  created_at?: string
  notification?: {
    audience: BigNewsAudienceMode
    target_user_count: number
    target_device_count: number
    push_requested: boolean
    published_at?: string | null
    delivery?: DeliverySummary | null
  }
}

const title = ref('')
const titleEn = ref('')
const html = ref('')
const htmlEn = ref('')
const pushBody = ref('')
const audienceMode = ref<BigNewsAudienceMode>('users')
const targetInput = ref('')
const sendPush = ref(true)
const preview = ref<BigNewsCampaignPreview | null>(null)
const previewing = ref(false)
const publishing = ref(false)
const historyLoading = ref(false)
const history = ref<CampaignHistoryItem[]>([])
const selectedHistoryItem = ref<CampaignHistoryItem | null>(null)

const historyDetailOpen = computed({
  get: () => Boolean(selectedHistoryItem.value),
  set: (open) => {
    if (!open) selectedHistoryItem.value = null
  },
})

const historyDetailHtml = computed(() =>
  selectedHistoryItem.value?.html || selectedHistoryItem.value?.content || '',
)

const historyDetailEnglishHtml = computed(() =>
  selectedHistoryItem.value?.html_en || selectedHistoryItem.value?.content_en || '',
)

const parsedTargets = computed(() =>
  Array.from(
    new Set(
      targetInput.value
        .split(/[\s,;]+/)
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  ),
)

const request = computed<BigNewsCampaignRequest>(() => ({
  title: title.value.trim(),
  title_en: titleEn.value.trim(),
  html: html.value.trim(),
  html_en: htmlEn.value.trim(),
  push_body: pushBody.value.trim(),
  audience: {
    mode: audienceMode.value,
    ...(audienceMode.value === 'users'
      ? { user_ids: parsedTargets.value.map(Number) }
      : {}),
    ...(audienceMode.value === 'devices'
      ? { device_uuids: parsedTargets.value }
      : {}),
  },
  send_push: sendPush.value,
}))

const targetLabel = computed(() => {
  if (audienceMode.value === 'broadcast') return '全部用户与访客'
  if (audienceMode.value === 'users') return `${parsedTargets.value.length} 个用户 ID`
  return `${parsedTargets.value.length} 个设备 UUID`
})

const previewHasResolutionErrors = computed(() => Boolean(
  preview.value?.unmatched_user_ids.length
  || preview.value?.unmatched_device_uuids.length
  || preview.value?.ambiguous_device_uuids.length,
))

const canPublish = computed(() => Boolean(
  request.value.title
  && request.value.html
  && preview.value
  && !previewHasResolutionErrors.value,
))

watch([audienceMode, targetInput], () => {
  preview.value = null
})

function validateForm() {
  if (!request.value.title || !request.value.html) {
    throw new Error('标题和正文不能为空')
  }
  if (audienceMode.value !== 'broadcast' && parsedTargets.value.length === 0) {
    throw new Error(audienceMode.value === 'users' ? '请输入至少一个用户 ID' : '请输入至少一个设备 UUID')
  }
  if (
    audienceMode.value === 'users'
    && parsedTargets.value.some((item) => !Number.isSafeInteger(Number(item)) || Number(item) <= 0)
  ) {
    throw new Error('用户 ID 必须是正整数')
  }
}

async function previewRecipients() {
  try {
    validateForm()
    previewing.value = true
    preview.value = await useAdmin.previewBigNewsCampaign(request.value)
    if (previewHasResolutionErrors.value) {
      toast({
        title: '收件范围未完全匹配',
        description: '请检查未匹配或归属冲突的目标后再发布',
        variant: 'destructive',
      })
    }
  } catch (error) {
    preview.value = null
    toast({
      title: '无法预览收件范围',
      description: errorMessage(error),
      variant: 'destructive',
    })
  } finally {
    previewing.value = false
  }
}

async function publishCampaign() {
  if (!preview.value || !canPublish.value) return
  const currentPreview = preview.value
  const confirmResult = await useDialog().confirm({
    title: audienceMode.value === 'broadcast' ? '发布全体通知' : '发布定向通知',
    body: confirmationBody(currentPreview),
    confirmButtonText: '确认发布',
    cancelButtonText: '返回检查',
    confirmButtonVariant: audienceMode.value === 'broadcast' ? 'destructive' : 'warning',
    defaultFocusButton: 'cancel',
  })
  if (confirmResult !== 'confirm') return

  publishing.value = true
  try {
    const result: any = await useAdmin.publishBigNewsCampaign({
      ...request.value,
      preview_signature: currentPreview.preview_signature,
    })
    const delivery = result?.delivery as DeliverySummary | undefined
    toast({
      title: delivery?.state === 'failed' ? '站内通知已发布，系统推送失败' : '通知已发布',
      description: deliveryDescription(delivery),
      variant: delivery?.state === 'failed' ? 'destructive' : undefined,
    })
    resetComposer()
    await loadHistory()
  } catch (error: any) {
    if (error?.data?.error === 'previewChanged' && error?.data?.preview) {
      preview.value = error.data.preview
    }
    toast({
      title: error?.data?.error === 'previewChanged' ? '收件范围已变化' : '发布失败',
      description: errorMessage(error),
      variant: 'destructive',
    })
  } finally {
    publishing.value = false
  }
}

async function loadHistory() {
  historyLoading.value = true
  try {
    history.value = await useAdmin.listBigNewsCampaigns({ limit: 30 })
  } catch (error) {
    toast({
      title: '通知记录加载失败',
      description: errorMessage(error),
      variant: 'destructive',
    })
  } finally {
    historyLoading.value = false
  }
}

async function expireCampaign(item: CampaignHistoryItem) {
  const result = await useDialog().confirm({
    title: '结束这条通知',
    body: `结束后，客户端下次刷新将不再显示「${item.title || `#${item.id}`}」。`,
    confirmButtonText: '结束通知',
    cancelButtonText: '取消',
    confirmButtonVariant: 'destructive',
    defaultFocusButton: 'cancel',
  })
  if (result !== 'confirm') return
  try {
    await useAdmin.expireBigNewsCampaign(item.id)
    item.expired = true
    await loadHistory()
    toast({ title: '通知已结束' })
  } catch (error) {
    toast({
      title: '操作失败',
      description: errorMessage(error),
      variant: 'destructive',
    })
  }
}

function openHistoryItem(item: CampaignHistoryItem) {
  selectedHistoryItem.value = item
}

function resetComposer() {
  title.value = ''
  titleEn.value = ''
  html.value = ''
  htmlEn.value = ''
  pushBody.value = ''
  targetInput.value = ''
  preview.value = null
}

function confirmationBody(value: BigNewsCampaignPreview) {
  const scope = value.audience.mode === 'broadcast'
    ? '所有能访问 bignews 的客户端'
    : value.audience.mode === 'users'
      ? `${value.matched_user_ids.length} 个用户`
      : `${value.target_devices.length} 个设备绑定`
  const push = sendPush.value
    ? `${value.push.eligible_device_count} 台已开启推送的设备（iOS ${value.push.platform_counts.ios || 0} / Android ${value.push.platform_counts.android || 0}）`
    : '不发送系统通知'
  return `站内显示范围：${scope}\n系统通知范围：${push}\n\n发布后内容会立即写入通知记录。`
}

function deliveryDescription(delivery?: DeliverySummary | null) {
  if (!delivery || delivery.state === 'skipped') return '已保存为站内通知，未发送系统通知'
  return `系统推送成功 ${delivery.sent || 0}，失败 ${(delivery.failed || 0) + (delivery.sender_failures || 0)}`
}

function audienceLabel(item: CampaignHistoryItem) {
  const notification = item.notification
  if (!notification || notification.audience === 'broadcast') return '全体'
  if (notification.audience === 'users') return `${notification.target_user_count} 位用户`
  return `${notification.target_device_count} 台设备`
}

function deliveryLabel(item: CampaignHistoryItem) {
  const delivery = item.notification?.delivery
  if (!item.notification?.push_requested) return '仅站内'
  if (!delivery) return '未知'
  if (delivery.state === 'pending') return '发送中'
  if (delivery.state === 'failed') return '发送失败'
  return `成功 ${delivery.sent || 0} / 失败 ${(delivery.failed || 0) + (delivery.sender_failures || 0)}`
}

function errorMessage(error: any) {
  return String(error?.data?.message || error?.message || error?.data || error || '未知错误')
}

function formatTime(value?: string | null) {
  if (!value) return '—'
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString()
}

onMounted(loadHistory)
</script>

<template lang="pug">
#notification-publisher(class="w-full px-1 py-4 md:px-4")
  header.publisher-header
    div
      p.eyebrow PANDAPUSH
      h1 通知推送
      p.subtitle 一份内容，同时成为客户端内的 bignews；需要时再唤起系统通知。
    Badge(variant="outline") {{ history.filter((item) => !item.expired).length }} 条进行中

  div.publisher-layout
    section.composer-panel
      div.section-heading
        span.step 01
        div
          h2 写通知
          p 中文为必填，英文为空时客户端回退到中文。

      div.form-grid
        div.field
          Label(for="notification-title") 中文标题
          Input#notification-title(v-model="title" maxlength="255" placeholder="一句话说明发生了什么")
        div.field
          Label(for="notification-title-en") English title
          Input#notification-title-en(v-model="titleEn" maxlength="255" placeholder="Optional")
        div.field.full
          div.editor-heading
            Label 中文正文
            span.field-note 富文本编辑，支持链接、图片与源码
          div.notification-editor-shell
            Tiptap(v-model="html" :editable="true" :autofocus="false")
        details.field.full.translation-details
          summary 添加英文正文
          div.editor-heading.mt-3
            Label English content
            span.field-note Optional
          div.notification-editor-shell.compact
            Tiptap(v-model="htmlEn" :editable="true" :autofocus="false")
        div.field.full
          Label(for="notification-push-body") 系统通知摘要
          Textarea#notification-push-body(
            v-model="pushBody"
            rows="3"
            placeholder="可留空，将从中文正文自动提取纯文本"
            class="resize-y"
          )

      div.section-heading.mt-8
        span.step 02
        div
          h2 选收件人
          p 用户定向会覆盖该账号的所有客户端；设备定向只显示在匹配设备。

      div.audience-grid
        div.field
          Label(for="notification-audience") 显示范围
          Select(v-model="audienceMode")
            SelectTrigger#notification-audience
              SelectValue
            SelectContent
              SelectItem(value="users") 指定用户
              SelectItem(value="devices") 指定设备
              SelectItem(value="broadcast") 全体用户与访客
        div.field(v-if="audienceMode !== 'broadcast'")
          Label(for="notification-targets") {{ audienceMode === 'users' ? '用户 ID' : '设备 UUID' }}
          Textarea#notification-targets(
            v-model="targetInput"
            rows="4"
            :placeholder="audienceMode === 'users' ? '42, 108, 256' : '每行一个 device UUID'"
            class="min-h-[104px] resize-y font-mono text-sm"
          )
          p.field-note 支持逗号、空格或换行分隔，自动去重。
        div.push-toggle
          Checkbox#notification-send-push(v-model="sendPush")
          div
            Label(for="notification-send-push") 同时发送系统通知
            p.field-note 尊重设备的推送开关；站内 bignews 始终保留。

      div.action-row
        div.scope-summary
          span 当前范围
          strong {{ targetLabel }}
        Button(variant="outline" :loading="previewing" @click="previewRecipients") 核对收件范围
        Button(:disabled="!canPublish" :loading="publishing" @click="publishCampaign") 发布通知

    aside.preview-column
      div.preview-sticky
        div.section-heading
          span.step 03
          div
            h2 最终预览
            p 客户端会先看到这条紧凑横幅。

        button.news-preview(type="button" disabled)
          span.news-mark !
          span.news-copy
            small 通知
            strong {{ title || '通知标题会显示在这里' }}
          Icon(name="material-symbols:chevron-right-rounded" class="h-5 w-5")

        div.article-preview
          div.article-title {{ title || '通知详情' }}
          div.article-body(v-if="html" v-html="html")
          p.article-empty(v-else) 输入正文后，这里会呈现详情内容。

        div.recipient-preview(v-if="preview")
          div.preview-metric
            span 系统推送设备
            strong {{ preview.push.eligible_device_count }}
          div.platform-split
            span iOS {{ preview.push.platform_counts.ios || 0 }}
            span Android {{ preview.push.platform_counts.android || 0 }}
            span(v-if="preview.push.opted_out_device_count") 已关闭 {{ preview.push.opted_out_device_count }}
          div.resolution-error(v-if="previewHasResolutionErrors")
            p(v-if="preview.unmatched_user_ids.length") 未找到用户：{{ preview.unmatched_user_ids.join(', ') }}
            p(v-if="preview.unmatched_device_uuids.length") 未找到设备：{{ preview.unmatched_device_uuids.join(', ') }}
            p(v-if="preview.ambiguous_device_uuids.length") 设备归属冲突：{{ preview.ambiguous_device_uuids.join(', ') }}
          div.target-list(v-else-if="preview.target_devices.length && audienceMode !== 'broadcast'")
            div.target-row(v-for="device in preview.target_devices" :key="`${device.user_id}-${device.platform}-${device.device_uuid}`")
              div
                strong {{ device.device_name || device.device_uuid }}
                small 用户 {{ device.user_id }} · {{ device.platform }}
              Badge(:variant="device.push_enabled ? 'secondary' : 'outline'") {{ device.push_enabled ? '可推送' : '仅站内' }}

  section.history-panel
    div.history-heading
      div
        p.eyebrow ARCHIVE
        h2 最近通知
      Button(size="sm" variant="ghost" :loading="historyLoading" @click="loadHistory") 刷新
    div.history-list(v-if="history.length")
      article.history-row(v-for="item in history" :key="item.id")
        button.history-open(type="button" @click="openHistoryItem(item)")
          div.history-id(v-text="'#' + item.id")
          div.history-main
            div.history-title
              strong {{ item.title || '未命名通知' }}
              Badge(:variant="item.expired ? 'outline' : 'secondary'") {{ item.expired ? '已结束' : '显示中' }}
            p {{ audienceLabel(item) }} · {{ deliveryLabel(item) }} · {{ formatTime(item.notification?.published_at || item.created_at) }}
          Icon(name="material-symbols:chevron-right-rounded" class="h-5 w-5")
        Button(v-if="!item.expired" size="sm" variant="ghost" @click.stop="expireCampaign(item)") 结束
    div.history-empty(v-else-if="!historyLoading") 还没有通知记录。先从一小组用户开始预览和发布。

  Sheet(v-model:open="historyDetailOpen")
    SheetContent(side="right" class="w-full overflow-y-auto p-0 sm:max-w-2xl")
      div.history-detail(v-if="selectedHistoryItem")
        SheetHeader.history-detail-header
          div.history-detail-kicker
            span(v-text="'#' + selectedHistoryItem.id")
            Badge(:variant="selectedHistoryItem.expired ? 'outline' : 'secondary'") {{ selectedHistoryItem.expired ? '已结束' : '显示中' }}
          SheetTitle {{ selectedHistoryItem.title || '未命名通知' }}
          SheetDescription {{ audienceLabel(selectedHistoryItem) }} · {{ deliveryLabel(selectedHistoryItem) }} · {{ formatTime(selectedHistoryItem.notification?.published_at || selectedHistoryItem.created_at) }}

        dl.history-detail-meta
          div
            dt 站内范围
            dd {{ audienceLabel(selectedHistoryItem) }}
          div
            dt 系统推送
            dd {{ deliveryLabel(selectedHistoryItem) }}
          div
            dt 发布时间
            dd {{ formatTime(selectedHistoryItem.notification?.published_at || selectedHistoryItem.created_at) }}

        section.history-detail-copy
          p.eyebrow CHINESE
          h3 {{ selectedHistoryItem.title || '中文内容' }}
          Tiptap(:model-value="historyDetailHtml" :editable="false" :autofocus="false")

        section.history-detail-copy(v-if="historyDetailEnglishHtml || selectedHistoryItem.title_en")
          p.eyebrow ENGLISH
          h3 {{ selectedHistoryItem.title_en || selectedHistoryItem.title || 'English content' }}
          Tiptap(:model-value="historyDetailEnglishHtml" :editable="false" :autofocus="false")

        div.delivery-error(v-if="selectedHistoryItem.notification?.delivery?.error")
          strong 推送错误
          p {{ selectedHistoryItem.notification.delivery.error }}

        Button(
          v-if="!selectedHistoryItem.expired"
          variant="outline"
          @click="expireCampaign(selectedHistoryItem)"
        ) 结束这条通知
</template>

<style scoped>
#notification-publisher {
  --notice-accent: oklch(0.72 0.16 61);
  --notice-accent-soft: color-mix(in oklch, var(--notice-accent) 14%, transparent);
  max-width: 1240px;
  margin: 0 auto;
  padding: 1rem 0.25rem;
  color: var(--foreground);
}

.publisher-header,
.history-heading,
.history-title,
.action-row,
.preview-metric,
.platform-split,
.target-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.publisher-header {
  align-items: flex-end;
  margin-bottom: 2rem;
}

.publisher-header h1 {
  margin: 0;
  color: var(--foreground);
  font-size: clamp(1.85rem, 3vw, 2.5rem);
  font-weight: 650;
  letter-spacing: -0.035em;
}

.eyebrow {
  margin: 0 0 0.25rem;
  color: var(--notice-accent);
  font-size: 0.6875rem;
  font-weight: 750;
  letter-spacing: 0.18em;
}

.subtitle,
.section-heading p,
.history-main p,
.field-note {
  color: var(--muted-foreground);
  font-size: 0.875rem;
}

.publisher-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 2.5rem;
}

@media (min-width: 1024px) {
  #notification-publisher {
    padding-inline: 1rem;
  }

  .publisher-layout {
    grid-template-columns: minmax(0, 1.45fr) minmax(320px, 0.75fr);
  }
}

.composer-panel,
.preview-column,
.history-panel {
  min-width: 0;
}

.section-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.section-heading h2,
.history-heading h2 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 650;
  letter-spacing: -0.015em;
}

.step {
  margin-top: 0.125rem;
  color: var(--notice-accent);
  font-size: 0.75rem;
  font-weight: 750;
}

.form-grid,
.audience-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
}

@media (min-width: 640px) {
  .form-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.field {
  display: grid;
  gap: 0.5rem;
}

.field.full {
  grid-column: 1 / -1;
}

.editor-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}

.editor-heading .field-note {
  margin: 0;
  text-align: right;
}

.notification-editor-shell {
  min-height: 220px;
  overflow: hidden;
  background: color-mix(in oklch, var(--background) 92%, var(--muted));
  border: 1px solid var(--border);
  border-radius: 0.55rem;
  transition:
    border-color 140ms ease,
    box-shadow 140ms ease;
}

.notification-editor-shell:focus-within {
  border-color: var(--notice-accent);
  box-shadow: 0 0 0 3px var(--notice-accent-soft);
}

.notification-editor-shell.compact {
  min-height: 170px;
}

.notification-editor-shell :deep(.tiptap-editor-container),
.notification-editor-shell :deep(.editor),
.notification-editor-shell :deep(.ProseMirror) {
  min-height: inherit;
}

.notification-editor-shell :deep(.ProseMirror) {
  padding: 1rem;
}

.translation-details {
  padding: 0.5rem 0.75rem;
  border: 1px dashed var(--border);
  border-radius: 0.55rem;
}

.translation-details summary {
  color: var(--muted-foreground);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
}

.push-toggle {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: color-mix(in oklch, var(--muted) 60%, transparent);
  border-radius: 0.55rem;
}

.action-row {
  flex-wrap: wrap;
  margin-top: 1.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--border);
}

.scope-summary {
  display: flex;
  flex-direction: column;
  margin-right: auto;
  font-size: 0.875rem;
}

.scope-summary span {
  color: var(--muted-foreground);
  font-size: 0.75rem;
}

.preview-sticky {
  display: grid;
  gap: 1.25rem;
}

@media (min-width: 1024px) {
  .preview-sticky {
    position: sticky;
    top: 5rem;
  }
}

.news-preview {
  display: flex;
  width: 100%;
  align-items: center;
  overflow: hidden;
  padding: 0;
  color: var(--foreground);
  text-align: left;
  background: var(--background);
  border: 2px solid var(--notice-accent);
  border-radius: 0.8rem;
  opacity: 1;
}

.news-mark {
  display: flex;
  width: 3.5rem;
  height: 3.5rem;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: oklch(0.99 0.01 70);
  background: var(--notice-accent);
  font-size: 1.25rem;
  font-weight: 900;
}

.news-copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  padding: 0.5rem 1rem;
}

.news-copy small {
  color: var(--notice-accent);
  font-size: 0.625rem;
  font-weight: 750;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.news-copy strong {
  overflow: hidden;
  font-size: 0.875rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.article-preview {
  min-height: 10rem;
  padding-block: 1.25rem;
  border-block: 1px solid var(--border);
}

.article-title {
  margin-bottom: 1rem;
  font-size: 1.25rem;
  font-weight: 650;
}

.article-body {
  max-height: 20rem;
  overflow-y: auto;
  font-size: 0.875rem;
  line-height: 1.75rem;
}

.article-body :deep(p) {
  margin-block: 0.5rem;
}

.article-body :deep(a) {
  color: var(--primary);
  text-decoration: underline;
  text-underline-offset: 0.25rem;
}

.article-body :deep(img) {
  max-width: 100%;
  margin-block: 0.75rem;
  border-radius: 0.5rem;
}

.article-empty,
.history-empty {
  color: var(--muted-foreground);
  font-size: 0.875rem;
}

.recipient-preview {
  display: grid;
  gap: 0.75rem;
  padding: 1rem;
  background: color-mix(in oklch, var(--muted) 60%, transparent);
  border-radius: 0.8rem;
}

.preview-metric strong {
  font-size: 1.5rem;
  font-weight: 650;
  font-variant-numeric: tabular-nums;
}

.platform-split {
  justify-content: flex-start;
  color: var(--muted-foreground);
  font-size: 0.75rem;
}

.resolution-error {
  display: grid;
  gap: 0.25rem;
  padding: 0.75rem;
  color: var(--destructive);
  background: color-mix(in oklch, var(--destructive) 10%, transparent);
  border-radius: 0.55rem;
  font-size: 0.75rem;
}

.target-list {
  display: grid;
  max-height: 12rem;
  gap: 0.5rem;
  overflow-y: auto;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border);
}

.target-row strong,
.target-row small {
  display: block;
}

.target-row strong {
  max-width: 13rem;
  overflow: hidden;
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.target-row small {
  color: var(--muted-foreground);
  font-size: 0.6875rem;
}

.history-panel {
  margin-top: 3rem;
  padding-top: 1.75rem;
  border-top: 1px solid var(--border);
}

.history-list {
  margin-top: 1rem;
}

.history-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  border-bottom: 1px solid var(--border);
}

.history-open {
  display: grid;
  grid-template-columns: 3rem minmax(0, 1fr) auto;
  align-items: center;
  min-width: 0;
  gap: 0.75rem;
  padding-block: 1rem;
  color: inherit;
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.history-open:hover .history-title strong {
  color: var(--notice-accent);
}

.history-open:focus-visible {
  outline: 2px solid var(--notice-accent);
  outline-offset: 3px;
}

.history-id {
  color: var(--muted-foreground);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.75rem;
}

.history-title {
  justify-content: flex-start;
}

.history-main p {
  margin-top: 0.25rem;
}

.history-detail {
  display: grid;
  gap: 1.75rem;
  padding: 2rem clamp(1rem, 4vw, 2.5rem) 3rem;
}

.history-detail-header {
  text-align: left;
}

.history-detail-kicker {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--muted-foreground);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.75rem;
}

.history-detail-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  padding-block: 1rem;
  border-block: 1px solid var(--border);
}

.history-detail-meta dt {
  color: var(--muted-foreground);
  font-size: 0.75rem;
}

.history-detail-meta dd {
  margin-top: 0.25rem;
  font-size: 0.875rem;
  font-weight: 600;
}

.history-detail-copy {
  min-width: 0;
}

.history-detail-copy h3 {
  margin-bottom: 0.75rem;
  font-size: 1.125rem;
  font-weight: 650;
}

.history-detail-copy :deep(.ProseMirror) {
  line-height: 1.75;
}

.delivery-error {
  padding: 1rem;
  color: var(--destructive);
  background: color-mix(in oklch, var(--destructive) 10%, transparent);
  border-radius: 0.55rem;
  font-size: 0.875rem;
}

@media (max-width: 639px) {
  #notification-publisher {
    padding-inline: 0.75rem;
  }

  .publisher-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .action-row {
    align-items: stretch;
  }

  .action-row > button {
    flex: 1;
  }

  .history-row {
    align-items: start;
  }

  .history-open {
    grid-template-columns: minmax(0, 1fr) auto;
  }

  .history-open .history-id {
    display: none;
  }

  .history-detail-meta {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
